import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Chip,
    Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BuildIcon from "@mui/icons-material/Build";
import UpdateIcon from "@mui/icons-material/Update";

import useAuthToken from '../functions/useAuthToken';
import { useApiClient } from "../components/useApiClient";

import type { AdminUserLookupDto, AdminUserSearchResultDto } from "../models/UserAdmin";
import type { AdminTenantSubscriptionDto, PlanOption, EnsureProvisionedResponse, AdminExtendSubscriptionRequest, AdminAssignPlanRequest, AdminTenantListDto } from "../models/TenantAdmin";
import { access } from "fs";

const formatDate = (value?: string | null) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
};

const percent = (used: number, max: number) => {
    if (!max || max <= 0) return 0;
    return Math.min(100, Math.round((used / max) * 100));
};

const TenantSeverityChip: React.FC<{ severity: string }> = ({ severity }) => {
    const color =
        severity === "critical"
            ? "error"
            : severity === "warning"
                ? "warning"
                : severity === "info"
                    ? "info"
                    : "success";

    const label =
        severity === "critical"
            ? "Critical"
            : severity === "warning"
                ? "Warning"
                : severity === "info"
                    ? "Info"
                    : "Healthy";

    return <Chip size="small" color={color as any} label={label} />;
};

const StatusChip: React.FC<{ active: boolean; label?: string | null }> = ({ active, label }) => (
    <Chip
        size="small"
        color={active ? "success" : "default"}
        label={label ?? (active ? "Active" : "Inactive")}
        variant={active ? "filled" : "outlined"}
    />
);

const UsageBar: React.FC<{ label: string; used: number; max: number }> = ({ label, used, max }) => {
    const pct = percent(used, max);
    const tone = pct >= 100 ? "error.main" : pct >= 85 ? "warning.main" : "success.main";

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {used} / {max || 0}
                </Typography>
            </Box>
            <Box sx={{ height: 10, borderRadius: 999, bgcolor: "action.hover", overflow: "hidden" }}>
                <Box sx={{ height: 10, width: `${pct}%`, bgcolor: tone }} />
            </Box>
        </Box>
    );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" gap={2}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" textAlign="right">
            {value}
        </Typography>
    </Box>
);

export default function AdminTenantPage() {
    const getAccessToken = useAuthToken();
    const { get, getWithParam, postJson, postUri } = useApiClient();

    const [tenantIdInput, setTenantIdInput] = useState("");
    const [userIdInput, setUserIdInput] = useState("");
    const [tenants, setTenants] = useState<AdminTenantListDto[]>([]);
    const [tenant, setTenant] = useState<AdminTenantSubscriptionDto | null>(null);
    const [plans, setPlans] = useState<PlanOption[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<number | "">("");
    const [monthsToAdd, setMonthsToAdd] = useState<number>(12);
    const [loading, setLoading] = useState(false);
    const [busyAction, setBusyAction] = useState<"extend" | "assign" | "repair" | null>(null);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [repairResult, setRepairResult] = useState<EnsureProvisionedResponse | null>(null);

    const [selectedTenantId, setSelectedTenantId] = useState<number | "">("");
    const parsedTenantId = selectedTenantId;

    const [userSearchText, setUserSearchText] = useState("");
    const [userOptions, setUserOptions] = useState<AdminUserSearchResultDto[]>([]);
    const [selectedUser, setSelectedUser] = useState<AdminUserSearchResultDto | null>(null);
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    const [userEmailInput, setUserEmailInput] = useState("");
    const [userLookup, setUserLookup] = useState<AdminUserLookupDto | null>(null);
    const parsedUserId = useMemo(() => Number(userIdInput), [userIdInput]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setMessage(null);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const tenantsRes = await get<AdminTenantListDto[]>(`admin/tenants/list`, accessToken);

            setTenants(tenantsRes);

        } catch (err: any) {
            setMessage({ type: "error", text: err?.message ?? "Failed to load tenants." });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (selectedTenantId) {
            loadTenant();
        }
    }, [selectedTenantId]);

    const loadPlans = async () => {
        if (plans.length) return;

        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token');

        const result = await get<PlanOption[]>("licensing/plans",accessToken);
        setPlans(result);
    };

    const loadTenant = async () => {
        setMessage(null);
        setRepairResult(null);

        if (!parsedTenantId || parsedTenantId <= 0) {
            setMessage({ type: "error", text: "Enter a valid tenant id." });
            return;
        }

        setLoading(true);
        try {
            await loadPlans();

            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await get<AdminTenantSubscriptionDto>(`admin/tenants/${parsedTenantId}/subscription`, accessToken);

            setTenant(result);
            setSelectedPlanId(result.licensingPlanId ?? "");
        } catch (err: any) {
            setTenant(null);
            setMessage({ type: "error", text: err?.message ?? "Failed to load tenant subscription." });
        } finally {
            setLoading(false);
        }
    };

    const lookupUserByEmail = async () => {
        setMessage(null);
        setRepairResult(null);
        setUserLookup(null);

        const email = userEmailInput.trim();
        if (!email) {
            setMessage({ type: "error", text: "Enter a user email." });
            return;
        }

        setBusyAction("repair");
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await getWithParam<AdminUserLookupDto>(
                "admin/users/by-email",
                accessToken,
                `email=${encodeURIComponent(email)};`
            );

            setUserLookup(result);
        } catch (err: any) {
            setUserLookup(null);
            setMessage({
                type: "error",
                text: err?.message ?? "User lookup failed."
            });
        } finally {
            setBusyAction(null);
        }
    };

    const searchUsers = async (query: string) => {
        const trimmed = query.trim();

        setUserSearchText(query);

        if (trimmed.length < 2) {
            setUserOptions([]);
            return;
        }

        setUserSearchLoading(true);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await get<AdminUserSearchResultDto[]>(
                `admin/users/search?q=${encodeURIComponent(trimmed)}`,accessToken
            );
            setUserOptions(result);
        } catch (err: any) {
            setUserOptions([]);
            setMessage({
                type: "error",
                text: err?.message ?? "User search failed."
            });
        } finally {
            setUserSearchLoading(false);
        }
    };

    const ensureProvisionedByLookup = async () => {
        if (!userLookup) return;

        setMessage(null);
        setRepairResult(null);
        setBusyAction("repair");

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await postUri<EnsureProvisionedResponse>(`admin/users/${userLookup.userId}/ensure-provisioned`, accessToken);

            setRepairResult(result);
            setMessage({
                type: "success",
                text: "Provisioning check completed."
            });

            if (result.defaultTenantId) {
                setTenantIdInput(String(result.defaultTenantId));
            }
        } catch (err: any) {
            setRepairResult(null);
            setMessage({
                type: "error",
                text: err?.message ?? "Failed to ensure provisioning."
            });
        } finally {
            setBusyAction(null);
        }
    };

    const ensureProvisionedBySelectedUser = async () => {
        if (!selectedUser) return;

        setMessage(null);
        setRepairResult(null);
        setBusyAction("repair");

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await postUri<EnsureProvisionedResponse>(
                `admin/users/${selectedUser.userId}/ensure-provisioned`,
                accessToken
            );

            setRepairResult(result);
            setMessage({
                type: "success",
                text: "Provisioning check completed."
            });

            if (result.defaultTenantId) {
                setSelectedTenantId(result.defaultTenantId);
            }
        } catch (err: any) {
            setRepairResult(null);
            setMessage({
                type: "error",
                text: err?.message ?? "Failed to ensure provisioning."
            });
        } finally {
            setBusyAction(null);
        }
    };

    const extendSubscription = async () => {
        if (!tenant) return;
        setBusyAction("extend");
        setMessage(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const extensionTerm: AdminExtendSubscriptionRequest = {
                monthsToAdd: monthsToAdd,
            }

            await postJson(`admin/tenants/${tenant.tenantId}/extend`, accessToken, extensionTerm);

            setMessage({ type: "success", text: `Subscription extended by ${monthsToAdd} month(s).` });
            await loadTenant();
        } catch (err: any) {
            setMessage({ type: "error", text: err?.message ?? "Failed to extend subscription." });
        } finally {
            setBusyAction(null);
        }
    };

    const assignPlan = async () => {
        if (!tenant || !selectedPlanId) return;
        setBusyAction("assign");
        setMessage(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const newPlan: AdminAssignPlanRequest = {
                licensingPlanId: selectedPlanId,
                endExistingActiveLicenseImmediately: true,
            }

            await postJson(`admin/tenants/${tenant.tenantId}/assign-plan`, accessToken, newPlan);

            setMessage({ type: "success", text: "Plan assigned successfully." });
            await loadTenant();
        } catch (err: any) {
            setMessage({ type: "error", text: err?.message ?? "Failed to assign plan." });
        } finally {
            setBusyAction(null);
        }
    };

    const ensureProvisioned = async () => {
        setMessage(null);
        if (!parsedUserId || parsedUserId <= 0) {
            setMessage({ type: "error", text: "Enter a valid user id." });
            return;
        }

        setBusyAction("repair");
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token');

            const result = await postUri<EnsureProvisionedResponse>(`admin/users/${parsedUserId}/ensure-provisioned`, accessToken);

            setRepairResult(result);
            setMessage({ type: "success", text: "Provisioning check completed." });
            if (result.defaultTenantId) {
                setTenantIdInput(String(result.defaultTenantId));
            }
        } catch (err: any) {
            setRepairResult(null);
            setMessage({ type: "error", text: err?.message ?? "Failed to ensure provisioning." });
        } finally {
            setBusyAction(null);
        }
    };

    return (
        <Box p={3}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5">Admin Subscription Tools</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Inspect a tenant subscription, extend the active term, assign a plan, or repair user provisioning.
                    </Typography>
                </Box>

                {message && <Alert severity={message.type}>{message.text}</Alert>}

                <Grid container spacing={3}>
                    <Grid sx={{ xs: 12, lg: 4 }}>
                        <Card>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6">Tenant Lookup</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Load the current subscription state for a tenant.
                                        </Typography>
                                    </Box>

                                    <Autocomplete
                                        options={tenants}
                                        getOptionLabel={(option) => option.name}
                                        value={tenants.find(t => t.tenantId === selectedTenantId) || null}
                                        onChange={(_, value) => setSelectedTenantId(value?.tenantId ?? "")}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Tenant" size="small" />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 2 }}>
                                                <Box>
                                                    <Typography variant="body2">{option.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.tierName ?? "No Plan"} · {option.licenseExpired
                                                            ? "Expired"
                                                            : option.isExpiringSoon
                                                                ? `${option.daysRemaining} days left`
                                                                : `${option.artworksUsed}/${option.artworksMax} artworks`}
                                                    </Typography>
                                                </Box>
                                                <TenantSeverityChip severity={option.severity} />
                                            </Box>
                                        )}
                                    />

                                    <Button
                                        variant="contained"
                                        startIcon={<SearchIcon />}
                                        onClick={loadTenant}
                                        disabled={loading}
                                    >
                                        {loading ? "Loading..." : "Load Tenant"}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid sx={{ xs: 12, lg: 4 }}>
                        <Card>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6">Extend Subscription</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Add months to the active tenant license.
                                        </Typography>
                                    </Box>

                                    <TextField
                                        select
                                        label="Months to Add"
                                        size="small"
                                        fullWidth
                                        value={monthsToAdd}
                                        onChange={(e) => setMonthsToAdd(Number(e.target.value))}
                                        disabled={!tenant}
                                    >
                                        {[1, 3, 6, 12].map((m) => (
                                            <MenuItem key={m} value={m}>
                                                {m} month{m > 1 ? "s" : ""}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <Button
                                        variant="outlined"
                                        startIcon={<UpdateIcon />}
                                        onClick={extendSubscription}
                                        disabled={!tenant || busyAction !== null}
                                    >
                                        {busyAction === "extend" ? "Extending..." : "Extend Subscription"}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid sx={{ xs: 12, lg: 4 }}>
                        <Card>
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6">Repair Provisioning</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Search for a user by email or display name, then ensure they have a tenant,
                                            license holder, default client, role, and location.
                                        </Typography>
                                    </Box>

                                    <Autocomplete
                                        options={userOptions}
                                        value={selectedUser}
                                        onChange={(_, value) => setSelectedUser(value)}
                                        getOptionLabel={(option) =>
                                            option.displayName
                                                ? `${option.displayName} (${option.email})`
                                                : option.email
                                        }
                                        filterOptions={(x) => x} // use server-side filtering only
                                        loading={userSearchLoading}
                                        isOptionEqualToValue={(option, value) => option.userId === value.userId}
                                        onInputChange={(_, value, reason) => {
                                            if (reason === "input") {
                                                searchUsers(value);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Find User"
                                                size="small"
                                                placeholder="Search by email or name"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {userSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                                <Typography variant="body2">
                                                    {option.displayName || option.email}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.email} · User ID {option.userId}
                                                </Typography>
                                            </Box>
                                        )}
                                    />

                                    {selectedUser && (
                                        <Alert severity={selectedUser.appearsProvisioned ? "info" : "warning"}>
                                            <Stack spacing={0.5}>
                                                <Typography variant="body2">
                                                    <strong>{selectedUser.displayName || selectedUser.email}</strong>
                                                </Typography>
                                                <Typography variant="body2">
                                                    User ID: {selectedUser.userId}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Default Tenant: {selectedUser.defaultTenantId ?? "—"}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Default Client: {selectedUser.defaultClientId || "—"}
                                                </Typography>
                                                <Typography variant="body2">
                                                    Provisioning Status: {selectedUser.appearsProvisioned ? "Appears complete" : "Needs attention"}
                                                </Typography>
                                            </Stack>
                                        </Alert>
                                    )}

                                    <Button
                                        variant="contained"
                                        startIcon={<BuildIcon />}
                                        onClick={ensureProvisionedBySelectedUser}
                                        disabled={!selectedUser || busyAction !== null}
                                    >
                                        {busyAction === "repair" ? "Running..." : "Ensure Provisioned"}
                                    </Button>

                                    {repairResult && (
                                        <Alert severity="success">
                                            User {repairResult.email} ensured. Default tenant:{" "}
                                            {repairResult.defaultTenantId ?? "—"}, default client:{" "}
                                            {repairResult.defaultClientId}.
                                        </Alert>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {tenant && (
                    <Grid container spacing={3}>
                        <Grid sx={{ xs: 12, lg: 5 }}>
                            <Card>
                                <CardContent>
                                    <Stack spacing={1.25}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="h6">Tenant Subscription</Typography>
                                            <StatusChip active={tenant.tenantIsActive} label={tenant.tenantIsActive ? "Tenant Active" : "Tenant Inactive"} />
                                        </Box>
                                        <Divider />
                                        <DetailRow label="Tenant" value={tenant.tenantName} />
                                        <DetailRow label="Tenant ID" value={tenant.tenantId} />
                                        <DetailRow label="Plan" value={tenant.planName ?? "—"} />
                                        <DetailRow label="Tier" value={tenant.tierName ?? "—"} />
                                        <DetailRow label="License Active" value={<StatusChip active={tenant.licenseIsActive} />} />
                                        <DetailRow label="Starts" value={formatDate(tenant.effectiveFromUtc)} />
                                        <DetailRow label="Ends" value={formatDate(tenant.effectiveToUtc)} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid sx={{ xs: 12, lg: 7 }}>
                            <Card>
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="h6">Usage</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Current tenant usage against the assigned subscription limits.
                                            </Typography>
                                        </Box>
                                        <UsageBar label="Clients" used={tenant.clientsUsed} max={tenant.clientsMax} />
                                        <UsageBar label="Portfolios" used={tenant.portfoliosUsed} max={tenant.portfoliosMax} />
                                        <UsageBar label="Artworks" used={tenant.artworksUsed} max={tenant.artworksMax} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid sx={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="h6">Assign Plan</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                End the active license and assign a new annual plan.
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={2} alignItems="center">
                                            <Grid sx={{ xs: 12, md: 8 }}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label="Licensing Plan"
                                                    value={selectedPlanId}
                                                    onChange={(e) => setSelectedPlanId(Number(e.target.value))}
                                                >
                                                    {plans.map((plan) => (
                                                        <MenuItem key={plan.licensingPlanId} value={plan.licensingPlanId}>
                                                            {plan.tierName} · {plan.planName} · Clients {plan.maxClients} · Portfolios {plan.maxPortfolios} · Artworks {plan.maxArtworks}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid sx={{ xs: 12, md: 4 }}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    onClick={assignPlan}
                                                    disabled={!selectedPlanId || busyAction !== null}
                                                >
                                                    {busyAction === "assign" ? "Assigning..." : "Assign Plan"}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Stack>
        </Box>
    );
}
